import { useMemo } from "react";
import { useApp } from "@/context/AppContext";
import { dateToStr, todayStr, eventsForDate } from "@/lib/timeUtils";
import { motion } from "framer-motion";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export function MonthView({ cursor, onSelectDay }) {
  const { events, sharedEvents, eventColor, settings } = useApp();
  const weekStart = settings?.week_start || "mon";
  const dowLabels = weekStart === "mon"
    ? ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]
    : ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const first = new Date(year, month, 1);
  let startOffset = first.getDay(); // 0 sun
  if (weekStart === "mon") startOffset = (startOffset + 6) % 7;
  const gridStart = new Date(year, month, 1 - startOffset);

  const cells = Array.from({ length: 42 }).map((_, i) => {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    return d;
  });
  const today = todayStr();
  const eventsByDate = useMemo(() => {
    const map = {};
    for (const d of cells) {
      const ds = dateToStr(d);
      map[ds] = { day: eventsForDate(events, ds), shared: eventsForDate(sharedEvents, ds) };
    }
    return map;
  }, [events, sharedEvents, year, month, weekStart]);

  return (
    <div className="flex flex-col h-full">
      <div className="grid grid-cols-7 mb-2">
        {dowLabels.map((d) => (
          <div key={d} className="text-center text-[11px] font-semibold uppercase tracking-widest py-1" style={{ color: "var(--text-muted)" }}>{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 grid-rows-6 gap-1.5 flex-1 min-h-0">
        {cells.map((d, i) => {
          const ds = dateToStr(d);
          const inMonth = d.getMonth() === month;
          const isToday = ds === today;
          const dayEvents = (eventsByDate[ds]?.day || []);
          const sharedDay = (eventsByDate[ds]?.shared || []);
          return (
            <motion.button
              key={i}
              whileHover={{ scale: 1.015 }} whileTap={{ scale: 0.985 }}
              onClick={() => onSelectDay(ds)}
              className="relative rounded-2xl p-2 text-left flex flex-col overflow-hidden transition-colors"
              style={{
                background: isToday ? "color-mix(in srgb, var(--accent-primary) 14%, var(--bg-card))" : "var(--bg-card)",
                border: `1px solid ${isToday ? "var(--accent-primary)" : "var(--border-subtle)"}`,
                opacity: inMonth ? 1 : 0.4,
              }}
              data-testid={`month-day-${ds}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-semibold tnum w-6 h-6 flex items-center justify-center rounded-full"
                  style={isToday ? { background: "var(--accent-primary)", color: "#fff" } : { color: "var(--text-primary)" }}>
                  {d.getDate()}
                </span>
                {dayEvents.length > 0 && (
                  <div className="flex gap-0.5">
                    {dayEvents.slice(0, 3).map((e, k) => <span key={k} className="w-1.5 h-1.5 rounded-full" style={{ background: eventColor(e) }} />)}
                  </div>
                )}
              </div>
              <div className="flex-1 space-y-1 overflow-hidden">
                {dayEvents.slice(0, 3).map((e) => (
                  <div key={e.id} className="text-[10px] truncate px-1.5 py-0.5 rounded-md font-medium"
                    style={{ background: `color-mix(in srgb, ${eventColor(e)} 22%, transparent)`, color: "var(--text-secondary)", borderLeft: `2px solid ${eventColor(e)}` }}>
                    {e.title}
                  </div>
                ))}
                {dayEvents.length > 3 && <div className="text-[9px] font-medium pl-1" style={{ color: "var(--text-muted)" }}>+{dayEvents.length - 3} more</div>}
                {sharedDay.slice(0, 2).map((e) => (
                  <div key={e.id} className="text-[10px] truncate px-1.5 py-0.5 rounded-md font-medium flex items-center gap-1"
                    style={{ color: "var(--text-muted)", border: `1px dashed ${e._share_color}`, background: `color-mix(in srgb, ${e._share_color} 10%, transparent)` }} title={`${e._owner_name}: ${e.title}`}>
                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: e._share_color }} />{e.title}
                  </div>
                ))}
              </div>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}

export { MONTHS };
